import {Trans} from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AccountsSetupGuideContainer} from '../../../components/accounts/accountsSetupGuide/accountsSetupGuideContainer';
import {accountsCustomFieldsLearnMoreLink} from '../../../helpers/accountHelpers';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {SettingsSidebarInfoBox} from '../../common/settingsSidebarInfoBox';

/*
 * Constants.
 */

const contactCustomFieldsLink = 'https://help.front.com/t/k929pr/custom-contact-fields';

/*
 * Types.
 */

interface CustomFieldsListSidebarProps {
  tabPage: SettingsCustomFieldsPagesEnum;
  onNavigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledInfoBoxesDiv = styled.div`
  flex-shrink: 1;

  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/*
 * Component.
 */

export const CustomFieldsListSidebar = ({tabPage, onNavigateToLink}: CustomFieldsListSidebarProps) => {
  switch (tabPage) {
    case SettingsCustomFieldsPagesEnum.CARDS:
      return (
        <SettingsSidebarInfoBox
          title={<Trans id="settings-contact-custom-fields-infobox-title">Contacts custom fields</Trans>}
          body={
            <>
              <p>
                <Trans id="settings-contact-custom-fields-infobox-body-1">
                  Front allows you to add more details to your contacts; such as job title or NPS score.
                </Trans>
              </p>
              <p>
                <Trans id="settings-contact-custom-fields-infobox-body-2">
                  These contact custom fields will then be shown to users in Contact Details and available
                  within rules to automate actions.
                </Trans>
              </p>
              <p>
                <Trans id="settings-contact-custom-fields-infobox-body-3">
                  They are also available for use as custom variables in message templates, and can be updated
                  via our API or through the third-party contact integration PieSync.
                </Trans>
              </p>
            </>
          }
          footer={<Trans id="settings-contact-custom-fields-infobox-footer">Learn More</Trans>}
          onFooterClick={() => onNavigateToLink(contactCustomFieldsLink)}
        />
      );
    case SettingsCustomFieldsPagesEnum.ACCOUNTS:
      return (
        <StyledInfoBoxesDiv>
          <AccountsSetupGuideContainer shouldHideCompletedGuide />
          <SettingsSidebarInfoBox
            title={<Trans id="settings-account-custom-fields-infobox-title">Account custom fields</Trans>}
            body={
              <p>
                <Trans id="settings-account-custom-fields-infobox-body">
                  Front allows you to add more details to your accounts; such as Revenue, Account Manager, or
                  Account Tier. These account custom fields will then be shown to users in Contact Details and
                  available within rules to automate actions.
                </Trans>
              </p>
            }
            footer={<Trans id="settings-account-custom-fields-infobox-footer">Learn More</Trans>}
            onFooterClick={() => onNavigateToLink(accountsCustomFieldsLearnMoreLink)}
          />
        </StyledInfoBoxesDiv>
      );
    default:
      return (
        <SettingsSidebarInfoBox
          title={<Trans id="settings-custom-fields-infobox-title">What are custom fields?</Trans>}
          body={
            <p>
              <Trans id="settings-custom-fields-infobox-body">
                Custom fields enable you to add more details to your resources that may be useful for your
                workflows.
              </Trans>
            </p>
          }
        />
      );
  }
};
